import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiUtils } from '../utils/api';

export default function ContactPageEditor() {
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(null);

  const [heroOpen, setHeroOpen] = useState(false);
  const [contactFormOpen, setContactFormOpen] = useState(false);

  const [hero, setHero] = useState({
    badge: '✨ Get In Touch',
    headingLine1: 'Let\'s Start a',
    headingLine2: 'Conversation',
    description: 'Have questions or need assistance? Our team is here to help you with inquiries, support, and partnership opportunities. Reach out and let\'s connect.',
    features: 'Fast Response • Dedicated Support • Global Reach',
    isActive: true,
  });

  const [contactForm, setContactForm] = useState({
    title: 'Let\'s Start a Conversation',
    subtitle: 'Whether you\'re looking to import, export, or explore new markets, our team is here to help. Fill out the form and we\'ll get back to you within 24 hours.',
    contactInfo: [
      {
        icon: 'map-pin',
        title: 'Visit Us',
        details: '123 Trade Center, Business District\nGlobal City, GC 12345',
      },
      {
        icon: 'phone',
        title: 'Call Us',
        details: '+1 (234) 567-890\nMon–Fri, 9:00 AM – 6:00 PM',
      },
      {
        icon: 'mail',
        title: 'Email Us',
        details: 'info@globaltrade.com\nsupport@globaltrade.com',
      },
    ],
    businessHours: [
      { day: 'Monday – Friday', hours: '9:00 AM – 6:00 PM' },
      { day: 'Saturday', hours: '10:00 AM – 4:00 PM' },
      { day: 'Sunday', hours: 'Closed' },
    ],
    businessHoursTitle: 'Business Hours',
    isActive: true,
  });

  const heroSectionId = useMemo(() => {
    const existing = (page?.sections || []).find(s => !s.isGlobal && s.sectionType === 'hero');
    return existing?.sectionId || 'hero-1';
  }, [page]);

  const contactFormSectionId = useMemo(() => {
    const existing = (page?.sections || []).find(s => !s.isGlobal && s.sectionType === 'contact-form');
    return existing?.sectionId || 'contact-form-1';
  }, [page]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = await apiUtils.cms.getPage('contact');

      setLoading(false);
      if (!res.success) {
        toast.error(res.error || 'Failed to load contact page');
        // Still allow editing/saving by initializing an empty page model
        setPage({ pageName: 'contact', metaTitle: '', metaDescription: '', sections: [] });
        return;
      }

      setPage(res.data);

      const heroSection = (res.data?.sections || []).find(s => s.sectionType === 'hero' && !s.isGlobal);
      const c = heroSection?.content || heroSection || {};
      setHero({
        badge: c.badge || '✨ Get In Touch',
        headingLine1: c.headingLine1 || 'Let\'s Start a',
        headingLine2: c.headingLine2 || 'Conversation',
        description: c.description || '',
        features: c.features || 'Fast Response • Dedicated Support • Global Reach',
        isActive: heroSection?.isActive ?? true,
      });

      const contactFormSection = (res.data?.sections || []).find(s => s.sectionType === 'contact-form' && !s.isGlobal);
      const cf = contactFormSection?.content || contactFormSection || {};
      setContactForm(prev => ({
        ...prev,
        title: cf.title || prev.title,
        subtitle: cf.subtitle || prev.subtitle,
        contactInfo: Array.isArray(cf.contactInfo) ? cf.contactInfo : prev.contactInfo,
        businessHours: Array.isArray(cf.businessHours) ? cf.businessHours : prev.businessHours,
        businessHoursTitle: cf.businessHoursTitle || prev.businessHoursTitle,
        isActive: contactFormSection?.isActive ?? true,
      }));
    })();
  }, []);

  const onChange = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setHero(prev => ({ ...prev, [key]: value }));
  };

  const onContactFormChange = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setContactForm(prev => ({ ...prev, [key]: value }));
  };

  const onSave = async () => {
    const safePage = page || { metaTitle: '', metaDescription: '', sections: [] };

    setLoading(true);
    try {
      // Build new sections array: upsert hero section, keep others unchanged.
      const existingSections = safePage.sections || [];
      const existingHeroIndex = existingSections.findIndex(s => !s.isGlobal && s.sectionType === 'hero');

      const nextHeroSection = {
        sectionId: heroSectionId,
        sectionType: 'hero',
        isGlobal: false,
        order: existingHeroIndex >= 0 ? existingSections[existingHeroIndex].order : 0,
        isActive: !!hero.isActive,
        content: {
          badge: hero.badge,
          headingLine1: hero.headingLine1,
          headingLine2: hero.headingLine2,
          description: hero.description,
          features: hero.features,
        },
        visualSettings: {},
      };

      const upsert = (arr, predicate, section) => {
        const idx = arr.findIndex(predicate);
        if (idx >= 0) return arr.map((s, i) => (i === idx ? section : s));
        return [section, ...arr];
      };

      let nextSections = upsert(existingSections, (s) => !s.isGlobal && s.sectionType === 'hero', nextHeroSection);

      const existingContactFormIndex = nextSections.findIndex(s => !s.isGlobal && s.sectionType === 'contact-form');
      const nextContactFormSection = {
        sectionId: contactFormSectionId,
        sectionType: 'contact-form',
        isGlobal: false,
        order: existingContactFormIndex >= 0 ? nextSections[existingContactFormIndex].order : 1,
        isActive: !!contactForm.isActive,
        content: {
          title: contactForm.title,
          subtitle: contactForm.subtitle,
          contactInfo: contactForm.contactInfo,
          businessHours: contactForm.businessHours,
          businessHoursTitle: contactForm.businessHoursTitle,
        },
        visualSettings: {},
      };

      nextSections = upsert(nextSections, (s) => !s.isGlobal && s.sectionType === 'contact-form', nextContactFormSection);

      // Normalize sections to match backend validation exactly.
      const normalizedSections = nextSections
        .slice()
        .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
        .map((s, idx) => {
          const isGlobal = !!s.isGlobal;
          const base = {
            sectionId: String(s.sectionId),
            order: idx,
            isActive: s.isActive !== false,
            isGlobal,
          };

          if (isGlobal) {
            return {
              ...base,
              globalSectionRef: String(s.globalSectionRef || '').toLowerCase(),
            };
          }

          return {
            ...base,
            sectionType: String(s.sectionType || '').toLowerCase(),
            content: s.content || {},
            visualSettings: s.visualSettings || {},
          };
        })
        // Drop invalid global refs to avoid 400s.
        .filter((s) => (s.isGlobal ? !!s.globalSectionRef : !!s.sectionType));

      const res = await apiUtils.cms.updatePage('contact', {
        metaTitle: safePage.metaTitle || '',
        metaDescription: safePage.metaDescription || '',
        sections: normalizedSections,
      });

      if (!res.success) {
        toast.error(res.error || 'Failed to save');
        return;
      }

      setPage(res.data);
      toast.success('Contact page saved');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Contact</h1>
        </div>
        <button
          onClick={onSave}
          disabled={loading}
          className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:opacity-60"
        >
          {loading ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div className="space-y-4">
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setHeroOpen(v => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            aria-expanded={heroOpen}
          >
            <div className="text-left">
              <div className="text-sm font-semibold text-gray-900">Hero</div>
              <div className="text-xs text-gray-500">sectionId: <code>{heroSectionId}</code></div>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${heroOpen ? 'rotate-180' : ''}`} />
          </button>

          {heroOpen && (
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs text-gray-500">Edit fields below</div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!hero.isActive} onChange={onChange('isActive')} />
                  Active
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Badge Text</label>
                  <input value={hero.badge} onChange={onChange('badge')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  <p className="text-xs text-gray-500 mt-1">e.g., "✨ Get In Touch"</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Heading Line 1</label>
                  <input value={hero.headingLine1} onChange={onChange('headingLine1')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Heading Line 2 (Gradient)</label>
                  <input value={hero.headingLine2} onChange={onChange('headingLine2')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea value={hero.description} onChange={onChange('description')} rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Features (separated by •)</label>
                  <input value={hero.features} onChange={onChange('features')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  <p className="text-xs text-gray-500 mt-1">e.g., "Fast Response • Dedicated Support • Global Reach"</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Contact Form Section */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setContactFormOpen(v => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            aria-expanded={contactFormOpen}
          >
            <div className="text-left">
              <div className="text-sm font-semibold text-gray-900">Contact Form & Info</div>
              <div className="text-xs text-gray-500">sectionId: <code>{contactFormSectionId}</code></div>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${contactFormOpen ? 'rotate-180' : ''}`} />
          </button>

          {contactFormOpen && (
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs text-gray-500">Edit contact form content</div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!contactForm.isActive} onChange={onContactFormChange('isActive')} />
                  Active
                </label>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                    <input value={contactForm.title} onChange={onContactFormChange('title')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                    <textarea value={contactForm.subtitle} onChange={onContactFormChange('subtitle')} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  </div>
                </div>

                {/* Contact Info */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-semibold text-gray-900">Contact Information</label>
                    <button
                      type="button"
                      onClick={() => setContactForm(prev => ({
                        ...prev,
                        contactInfo: [...prev.contactInfo, { icon: 'map-pin', title: '', details: '' }],
                      }))}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Add Contact Info
                    </button>
                  </div>
                  <div className="space-y-3">
                    {(contactForm.contactInfo || []).map((info, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-sm font-medium text-gray-900">Contact #{idx + 1}</div>
                          <button
                            type="button"
                            onClick={() => setContactForm(prev => ({
                              ...prev,
                              contactInfo: prev.contactInfo.filter((_, i) => i !== idx),
                            }))}
                            className="px-3 py-1 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                            <select
                              value={info.icon || 'map-pin'}
                              onChange={(e) => {
                                const newInfo = [...contactForm.contactInfo];
                                newInfo[idx] = { ...newInfo[idx], icon: e.target.value };
                                setContactForm(prev => ({ ...prev, contactInfo: newInfo }));
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            >
                              <option value="map-pin">Map Pin</option>
                              <option value="phone">Phone</option>
                              <option value="mail">Mail</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                            <input
                              value={info.title || ''}
                              onChange={(e) => {
                                const newInfo = [...contactForm.contactInfo];
                                newInfo[idx] = { ...newInfo[idx], title: e.target.value };
                                setContactForm(prev => ({ ...prev, contactInfo: newInfo }));
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Details (use \n for line breaks)</label>
                            <textarea
                              value={info.details || ''}
                              onChange={(e) => {
                                const newInfo = [...contactForm.contactInfo];
                                newInfo[idx] = { ...newInfo[idx], details: e.target.value };
                                setContactForm(prev => ({ ...prev, contactInfo: newInfo }));
                              }}
                              rows={2}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Business Hours */}
                <div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Business Hours Title</label>
                    <input value={contactForm.businessHoursTitle} onChange={onContactFormChange('businessHoursTitle')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  </div>
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">Hours</label>
                      <button
                        type="button"
                        onClick={() => setContactForm(prev => ({
                          ...prev,
                          businessHours: [...prev.businessHours, { day: '', hours: '' }],
                        }))}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Add Hours
                      </button>
                    </div>
                    <div className="space-y-2">
                      {(contactForm.businessHours || []).map((hour, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <input
                            value={hour.day || ''}
                            onChange={(e) => {
                              const newHours = [...contactForm.businessHours];
                              newHours[idx] = { ...newHours[idx], day: e.target.value };
                              setContactForm(prev => ({ ...prev, businessHours: newHours }));
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                            placeholder="Day(s)"
                          />
                          <input
                            value={hour.hours || ''}
                            onChange={(e) => {
                              const newHours = [...contactForm.businessHours];
                              newHours[idx] = { ...newHours[idx], hours: e.target.value };
                              setContactForm(prev => ({ ...prev, businessHours: newHours }));
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                            placeholder="Hours"
                          />
                          <button
                            type="button"
                            onClick={() => setContactForm(prev => ({
                              ...prev,
                              businessHours: prev.businessHours.filter((_, i) => i !== idx),
                            }))}
                            className="px-3 py-2 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
